import { Bar } from 'react-chartjs-2';
import {
  ScenarioDataSet,
  ScenarioDataSetLabelV2,
  ScenarioQueryParams,
  useGetScenarioStatisticsQuery,
} from '@customer/store/apis/scenariosApi';
import { useAppSelector } from '@customer/store/hooks';
import { useCallback, useMemo } from 'react';
import {
  getChartOptions,
  getColorScheme,
  getChartHeight,
  GetTooltipTitle,
  BAR_THICKNESS,
} from '../utils';
import { BarChartByProps, ChartColorScheme } from '../config';
import { useTranslation } from 'react-i18next';
import useFilters from '@customer/filters/useFilters';
import { useLocationFilterParams } from '@customer/store/slices/locationFilterSlice';
import { useChartScenarioTimeFilters } from '../hooks';
import {
  barSelectPlugin,
  CustomBarChartData,
  CustomBarChartDataset,
  CustomBarChartLabel,
  OnBarSelect,
  OnCategorySelect,
  OnGetValue,
} from '../bar-select/BarSelectPlugin';
import { ChartOptions } from 'chart.js';
import ChartManager from '../chart-manager/ChartManager';
import { useLocaleFormatter } from '@customer/shared/hooks/useLocaleFormatter';

export interface BarChartScenarioProps extends BarChartByProps {
  getCategoryLabel?: (label: ScenarioDataSetLabelV2) => string;
  getDataSetLabel?: (id: string) => string;
  getTooltipTitle?: GetTooltipTitle;
  stacked?: boolean;
  onCategorySelect?: OnCategorySelect;
  onBarSelect?: OnBarSelect;
}

export const BarChartScenario = <FiltersSubset extends ScenarioQueryParams>({
  scenario,
  getCategoryLabel,
  getDataSetLabel,
  getTooltipTitle,
  onCategorySelect,
  onBarSelect,
  colorScheme,
  customColorSchemeFunc,
  includeTimeRange = false,
  timeRange,
  histogram = false,
  tooltipTransKey,
  includeAssetId = false,
  assetId,
  filterGroup,
  stacked = true,
}: BarChartScenarioProps) => {
  const { t } = useTranslation();
  const { getNumberText } = useLocaleFormatter();

  const { siteId, dateTimeFormat } = useAppSelector(state => state.auth);
  const darkTheme = useAppSelector(state => state.prefs.darkTheme);

  const timeRangeFilters = useChartScenarioTimeFilters({
    scenario,
    includeTimeRange,
    timeRange,
  });

  const filters = useFilters<FiltersSubset>(filterGroup);

  const locationFilters = useLocationFilterParams({
    filterGroup,
    includeAssetId,
    assetId,
  });

  // Skip if assetId is expected but undefined (loading) for charts that need it.
  const assetIdIsMissing = includeAssetId && typeof assetId === 'undefined';

  const { data: chartData, isFetching } = useGetScenarioStatisticsQuery(
    {
      scenario,
      query: {
        ...filters,
        ...locationFilters,
        ...timeRangeFilters,
        assetIds: includeAssetId ? assetId : undefined,
      },
    },
    {
      refetchOnMountOrArgChange: 60,
      skip: typeof siteId === 'undefined' || assetIdIsMissing,
    },
  );

  // { labels: string[], datasets: CustomDataset[]}
  const data = useMemo<CustomBarChartData>(() => {
    let displayLabels: CustomBarChartLabel[] = [];
    const dataSets: CustomBarChartDataset[] = [];

    if (chartData) {
      // Process the category labels.
      // Add a translated label property to the label object, but keep the source value for identification.
      displayLabels = chartData.labels.map((label: ScenarioDataSetLabelV2) => ({
        ...label,
        label: getCategoryLabel ? getCategoryLabel(label) : label.name,
      }));

      // Determine the color scheme for the chart.
      const stackedColors =
        colorScheme === ChartColorScheme.Custom && typeof customColorSchemeFunc !== 'undefined'
          ? customColorSchemeFunc(chartData)
          : getColorScheme(colorScheme);

      // Process the individual datasets.
      for (let i = 0; i < chartData.dataSets.length; i++) {
        const item: ScenarioDataSet = chartData.dataSets[i];

        // Determine the colors for this dataset.
        const themeColors = stackedColors[i % stackedColors.length];
        const backgroundColor = darkTheme ? themeColors[1] : themeColors[0];

        // Translate and modify the label if a function was provided.
        const label = getDataSetLabel?.(item.label) ?? item.label;

        // If stacked, assign the data to the same stack string, otherwise assign to unique stack strings.
        const stack = stacked ? 'group' : `individual--${i}`;

        // Add percentage-based sizing for histograms to allow for horizontal resizing.
        const barSizeProps = histogram
          ? { barPercentage: 1, categoryPercentage: 0.95 }
          : { barThickness: BAR_THICKNESS };

        dataSets.push({
          ...barSizeProps,
          label,
          data: item.values,
          backgroundColor,
          stack,
          labelSource: item.label, // Custom dataset option to keep the un-translated label for identification.
        });
      }
    }

    return {
      labels: displayLabels,
      datasets: dataSets,
    };
  }, [
    darkTheme,
    colorScheme,
    customColorSchemeFunc,
    chartData,
    getDataSetLabel,
    getCategoryLabel,
    stacked,
    histogram,
  ]);

  const wrappedTooltipTitle = useCallback<GetTooltipTitle>(
    (index, category, dataSetLabel) => {
      return getTooltipTitle ? getTooltipTitle(index, category, dataSetLabel) : category;
    },
    [getTooltipTitle],
  );

  const wrappedGetNumberText = useCallback<OnGetValue>(
    (value: number | string) => {
      return typeof value === 'number' ? getNumberText(value) : value;
    },
    [getNumberText],
  );

  const chartOptions = useMemo<ChartOptions<'bar'>>(() => {
    return getChartOptions(
      darkTheme,
      histogram,
      t,
      tooltipTransKey,
      dateTimeFormat,
      wrappedTooltipTitle,
      wrappedGetNumberText,
      onCategorySelect,
      onBarSelect,
    );
  }, [
    darkTheme,
    histogram,
    t,
    tooltipTransKey,
    dateTimeFormat,
    wrappedTooltipTitle,
    onCategorySelect,
    onBarSelect,
    wrappedGetNumberText,
  ]);

  const numLabels = data?.labels?.length ?? 0;
  const numDataSets = data?.datasets?.length ?? 0;

  const fixedHeight = getChartHeight(numLabels, numDataSets, histogram, stacked);

  return (
    <ChartManager
      isLoading={typeof chartData === 'undefined'}
      isFetching={isFetching}
      hasData={numLabels > 0}
      height={fixedHeight}
      fallbackHeight={200}
      trimChartHeight
    >
      <Bar options={chartOptions} data={data} plugins={[barSelectPlugin]} />
    </ChartManager>
  );
};

export default BarChartScenario;
